<div>
  <!-- Hero Section -->
  <section class="bg-gradient-to-r from-brand-primary via-purple-600 to-brand-accent text-white py-24 px-4">
    <div class="container mx-auto">
      <div class="max-w-4xl">
        <h1 class="text-5xl md:text-6xl font-bold mb-6 leading-tight">
          Mobile Videowand in Leipzig mieten
        </h1>
        <p class="text-xl md:text-2xl mb-6 text-white/90">
          Hochauflösend • Wetterfest • Outdoor-tauglich • 2,6mm Pixelpitch
        </p>
        <p class="text-lg mb-8 text-white/85 max-w-2xl">
          Professionelle LED-Videowand für Ihre Events, Messen und Stadtfeste in Leipzig und Umgebung. 
          Bestechend klare Bildqualität mit kurzen Betrachtungsabständen. Ideal für Firmen, Städte und große Veranstaltungen.
        </p>
        <div class="flex flex-col sm:flex-row gap-4">
          <button
            (click)="navigateToBooking()"
            class="bg-white text-brand-primary font-bold px-8 py-4 rounded-lg hover:shadow-2xl hover:scale-105 transition-all duration-300"
          >
            Verfügbarkeit prüfen
          </button>
          <a
            href="#was-sie-mieten"
            class="inline-block border-2 border-white text-white font-bold px-8 py-4 rounded-lg hover:bg-white hover:text-brand-primary transition-all duration-300"
          >
            Mehr erfahren
          </a>
        </div>
      </div>
    </div>
  </section>

  <!-- Kurzbeschreibung: Was Sie bekommen -->
  <section id="was-sie-mieten" class="py-16 bg-white">
    <div class="container mx-auto px-4">
      <div class="max-w-4xl mx-auto">
        <h2 class="text-3xl font-bold mb-8 text-gray-900">Was Sie mieten</h2>
        <div class="grid md:grid-cols-2 gap-8">
          <div class="space-y-4">
            <h3 class="text-xl font-bold text-brand-primary">Technik-Eckdaten</h3>
            <ul class="space-y-3 text-gray-700">
              <li class="flex items-start gap-3">
                <span class="text-brand-accent font-bold mt-1">✓</span>
                <span><strong>Display-Größe:</strong> 2,5 m × 1,5 m</span>
              </li>
              <li class="flex items-start gap-3">
                <span class="text-brand-accent font-bold mt-1">✓</span>
                <span><strong>Pixelpitch:</strong> 2,6 mm für kristallklare Bilder</span>
              </li>
              <li class="flex items-start gap-3">
                <span class="text-brand-accent font-bold mt-1">✓</span>
                <span><strong>Helligkeit:</strong> 4.500 Nits (sehr hell, auch bei Sonne lesbar)</span>
              </li>
              <li class="flex items-start gap-3">
                <span class="text-brand-accent font-bold mt-1">✓</span>
                <span><strong>Stromversorgung:</strong> 230V / 16A</span>
              </li>
              <li class="flex items-start gap-3">
                <span class="text-brand-accent font-bold mt-1">✓</span>
                <span><strong>Transportmaße:</strong> 2,66 m × 2,10 m × 0,96 m</span>
              </li>
              <li class="flex items-start gap-3">
                <span class="text-brand-accent font-bold mt-1">✓</span>
                <span><strong>Vollständig wetterfest</strong> für Outdoor-Einsatz</span>
              </li>
            </ul>
          </div>
          <div class="space-y-4">
            <h3 class="text-xl font-bold text-brand-primary">Unsere Leistung</h3>
            <ul class="space-y-3 text-gray-700">
              <li class="flex items-start gap-3">
                <span class="text-brand-accent font-bold mt-1">🚚</span>
                <span>Lieferung und Transport (separat berechnet)</span>
              </li>
              <li class="flex items-start gap-3">
                <span class="text-brand-accent font-bold mt-1">🔧</span>
                <span>Professioneller Aufbau und technische Einweisung</span>
              </li>
              <li class="flex items-start gap-3">
                <span class="text-brand-accent font-bold mt-1">📺</span>
                <span>Unterstützung bei Content-Zuspielung (HDMI, MediaPlayer)</span>
              </li>
              <li class="flex items-start gap-3">
                <span class="text-brand-accent font-bold mt-1">⚙️</span>
                <span>Technischer Support während der Veranstaltung</span>
              </li>
              <li class="flex items-start gap-3">
                <span class="text-brand-accent font-bold mt-1">✨</span>
                <span>Abbau und Rückholung nach Einsatz</span>
              </li>
              <li class="flex items-start gap-3">
                <span class="text-brand-accent font-bold mt-1">📍</span>
                <span>Lokal in Leipzig – schnelle Reaktion</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </section>

  <!-- Einsatzbereiche -->
  <section id="einsatzbereiche" class="py-16 bg-gradient-to-br from-brand-primary/5 to-brand-accent/5">
    <div class="container mx-auto px-4">
      <h2 class="text-4xl font-bold text-center mb-4 text-gray-900">Einsatzbereiche</h2>
      <p class="text-center text-gray-700 mb-12 max-w-2xl mx-auto">
        Unsere Mobile Videowand ist vielseitig einsetzbar – für kleine und große Veranstaltungen
      </p>

      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
        <div *ngFor="let useCase of useCases" class="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition">
          <h3 class="text-xl font-bold text-brand-primary mb-3">{{ useCase.title }}</h3>
          <p class="text-gray-600">{{ useCase.description }}</p>
        </div>
      </div>
    </div>
  </section>

  <!-- Warum 2,6mm Pixelpitch wichtig ist -->
  <section class="py-16 bg-white">
    <div class="container mx-auto px-4">
      <div class="max-w-4xl mx-auto">
        <h2 class="text-4xl font-bold mb-8 text-gray-900">
          Warum ein 2,6mm Pixelpitch so wichtig ist
        </h2>
        <p class="text-lg text-gray-700 mb-8">
          Der Pixelpitch ist der Abstand zwischen einzelnen LED-Pixeln. Je kleiner dieser Abstand, desto klarer 
          und schärfer die Bilddarstellung – besonders bei kurzen Betrachtungsabständen.
        </p>

        <div class="bg-gradient-to-r from-brand-primary/10 to-brand-accent/10 rounded-xl p-8 mb-8">
          <h3 class="text-2xl font-bold text-brand-primary mb-6">Mit 2,6mm Pixelpitch sehen Sie:</h3>
          <ul class="space-y-4">
            <li class="flex items-start gap-4">
              <span class="text-3xl">📊</span>
              <div>
                <p class="font-bold text-gray-900 mb-1">Präsentationen und Diagramme</p>
                <p class="text-gray-600">Kleinere Schriften und Tabellen bleiben lesbar und professionell</p>
              </div>
            </li>
            <li class="flex items-start gap-4">
              <span class="text-3xl">📝</span>
              <div>
                <p class="font-bold text-gray-900 mb-1">Text und Logos</p>
                <p class="text-gray-600">Auch detaillierte Texte und Firmenlogos wirken gestochen scharf</p>
              </div>
            </li>
            <li class="flex items-start gap-4">
              <span class="text-3xl">🎬</span>
              <div>
                <p class="font-bold text-gray-900 mb-1">Videos in HD-Qualität</p>
                <p class="text-gray-600">Live-Streams und vorproduzierte Videos in beeindruckender Qualität</p>
              </div>
            </li>
            <li class="flex items-start gap-4">
              <span class="text-3xl">👁️</span>
              <div>
                <p class="font-bold text-gray-900 mb-1">Angenehme Betrachtung</p>
                <p class="text-gray-600">Keine Pixelmosaike – auch beim näheren Hinschauen bleibt das Bild homogen</p>
              </div>
            </li>
          </ul>
        </div>

        <p class="text-gray-700 leading-relaxed">
          Dies unterscheidet unsere Videowand von größerpixeligen LED-Trailern (4mm–5mm Pixelpitch), 
          bei denen Text und kleine Details verschwimmen. Mit 2,6mm Pixelpitch erzielen Sie professionelle Ergebnisse.
        </p>
      </div>
    </div>
  </section>

  <!-- Technik & Voraussetzungen -->
  <section class="py-16 bg-gradient-to-br from-blue-50 to-indigo-50">
    <div class="container mx-auto px-4">
      <div class="max-w-4xl mx-auto">
        <h2 class="text-4xl font-bold mb-8 text-gray-900">Technik & Voraussetzungen</h2>

        <div class="grid md:grid-cols-2 gap-8 mb-8">
          <div class="bg-white rounded-xl p-6 shadow">
            <h3 class="text-2xl font-bold text-brand-primary mb-6">Technische Spezifikationen</h3>
            <div class="space-y-4">
              <div>
                <p class="font-bold text-gray-900">Display-Größe</p>
                <p class="text-gray-600">2,5 m (B) × 1,5 m (H)</p>
              </div>
              <div>
                <p class="font-bold text-gray-900">Pixelpitch</p>
                <p class="text-gray-600">2,6 mm für hochauflösende Darstellung</p>
              </div>
              <div>
                <p class="font-bold text-gray-900">Helligkeit (Luminanz)</p>
                <p class="text-gray-600">4.500 Nits – auch bei Sonnenlicht sehr hell</p>
              </div>
              <div>
                <p class="font-bold text-gray-900">Stromversorgung</p>
                <p class="text-gray-600">230V AC, max. 16A (Standard-Haushaltsstrom)</p>
              </div>
              <div>
                <p class="font-bold text-gray-900">Gewicht (Transportkiste)</p>
                <p class="text-gray-600">ca. 450–500 kg</p>
              </div>
            </div>
          </div>

          <div class="bg-white rounded-xl p-6 shadow">
            <h3 class="text-2xl font-bold text-brand-primary mb-6">Was Sie vorbereiten müssen</h3>
            <div class="space-y-4">
              <div class="flex items-start gap-3">
                <span class="text-2xl">✓</span>
                <div>
                  <p class="font-bold text-gray-900">Ebene Aufstellfläche</p>
                  <p class="text-gray-600 text-sm">Asphalt, Betonboden oder flacher Rasen</p>
                </div>
              </div>
              <div class="flex items-start gap-3">
                <span class="text-2xl">✓</span>
                <div>
                  <p class="font-bold text-gray-900">Stromzugang</p>
                  <p class="text-gray-600 text-sm">230V / 16A Steckdose in Reichweite</p>
                </div>
              </div>
              <div class="flex items-start gap-3">
                <span class="text-2xl">✓</span>
                <div>
                  <p class="font-bold text-gray-900">Zufahrt für Transport</p>
                  <p class="text-gray-600 text-sm">Zugang für Transporter (mind. 2,5 m Breite)</p>
                </div>
              </div>
              <div class="flex items-start gap-3">
                <span class="text-2xl">✓</span>
                <div>
                  <p class="font-bold text-gray-900">Content vorbereitet</p>
                  <p class="text-gray-600 text-sm">Gerne beraten wir Sie zur Aufbereitung</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="bg-white rounded-xl p-8 shadow border-l-4 border-brand-accent">
          <h3 class="text-xl font-bold text-brand-primary mb-4">🔧 Unsere Arbeit für Sie</h3>
          <p class="text-gray-700 mb-4">
            Machen Sie sich keine Sorgen um technische Details. Unser Team kümmert sich um:
          </p>
          <ul class="grid md:grid-cols-2 gap-4 text-gray-700">
            <li class="flex items-start gap-2">
              <span class="text-brand-accent font-bold">→</span>
              <span>Transport und Anlieferung</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-brand-accent font-bold">→</span>
              <span>Auf- und Abbau</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-brand-accent font-bold">→</span>
              <span>Komplette Inbetriebnahme und Einweisung</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-brand-accent font-bold">→</span>
              <span>Technischer Support während der Veranstaltung</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </section>

  <!-- Preise & Mietdauer -->
  <section class="py-16 bg-white">
    <div class="container mx-auto px-4">
      <div class="max-w-4xl mx-auto">
        <h2 class="text-4xl font-bold mb-4 text-center text-gray-900">Preise & Mietdauer</h2>
        <p class="text-center text-gray-600 mb-12">
          Transparente Preisgestafflung – je länger, desto günstiger
        </p>

        <div class="grid md:grid-cols-3 gap-6 mb-8">
          <!-- Tagesmiete -->
          <div class="bg-gradient-to-br from-brand-primary/5 to-white border-2 border-brand-primary rounded-xl p-6 hover:shadow-lg transition">
            <h3 class="text-2xl font-bold text-brand-primary mb-2">Tagesmiete</h3>
            <p class="text-gray-600 text-sm mb-6">1 Tag</p>
            <div class="mb-6">
              <span class="text-4xl font-bold text-brand-accent">490 €</span>
              <span class="text-gray-600"> / Tag</span>
            </div>
            <p class="text-sm text-gray-700 mb-6">
              Ideal für einmalige Events und Veranstaltungen
            </p>
            <button
              (click)="navigateToBooking()"
              class="w-full bg-brand-primary text-white font-bold py-3 rounded-lg hover:bg-brand-primary/90 transition"
            >
              Jetzt verbindlich buchen
            </button>
          </div>

          <!-- Wochenmiete -->
          <div class="bg-gradient-to-br from-brand-accent/10 to-white border-2 border-brand-accent rounded-xl p-6 shadow-lg hover:shadow-xl transition transform hover:scale-105">
            <div class="flex items-center gap-2 mb-2">
              <h3 class="text-2xl font-bold text-brand-accent">Wochenmiete</h3>
              <span class="bg-brand-accent text-white text-xs font-bold px-3 py-1 rounded-full">BELIEBT</span>
            </div>
            <p class="text-gray-600 text-sm mb-6">7 Tage (rabattiert)</p>
            <div class="mb-6">
              <span class="text-4xl font-bold text-brand-accent">343 €</span>
              <span class="text-gray-600"> / Tag</span>
            </div>
            <p class="text-sm text-gray-700 mb-4">
              <strong>Gesamtpreis: ca. 2.400 €</strong>
            </p>
            <p class="text-sm text-gray-700 mb-6">
              Perfekt für längere Events, Messen oder Kampagnen
            </p>
            <button
              (click)="navigateToBooking()"
              class="w-full bg-brand-accent text-white font-bold py-3 rounded-lg hover:bg-brand-accent/90 transition"
            >
              Jetzt verbindlich buchen
            </button>
          </div>

          <!-- Langzeitmiete -->
          <div class="bg-gradient-to-br from-purple-100 to-white border-2 border-purple-500 rounded-xl p-6 hover:shadow-lg transition">
            <h3 class="text-2xl font-bold text-purple-700 mb-2">Langzeitmiete</h3>
            <p class="text-gray-600 text-sm mb-6">14 Tage+ (optimal rabattiert)</p>
            <div class="mb-6">
              <span class="text-4xl font-bold text-purple-600">270 €</span>
              <span class="text-gray-600"> / Tag</span>
            </div>
            <p class="text-sm text-gray-700 mb-4">
              <strong>Beispiel 14 Tage: ca. 3.780 €</strong>
            </p>
            <p class="text-sm text-gray-700 mb-6">
              Für Langzeitprojekte, Baustellen-Kommunikation oder längere Kampagnen
            </p>
            <button
              (click)="navigateToBooking()"
              class="w-full bg-purple-600 text-white font-bold py-3 rounded-lg hover:bg-purple-700 transition"
            >
              Jetzt verbindlich buchen
            </button>
          </div>
        </div>

        <div class="bg-blue-50 border-l-4 border-blue-400 rounded-lg p-6 mb-8">
          <h4 class="font-bold text-blue-900 mb-3">💡 Wichtige Hinweise zu den Preisen:</h4>
          <ul class="text-sm text-blue-800 space-y-2">
            <li>• <strong>Transport & Lieferung werden separat berechnet</strong> – abhängig von Ihrer Entfernung zu Leipzig</li>
            <li>• Auf-/Abbau, Inbetriebnahme und Einweisung sind in diesen Preisen enthalten</li>
            <li>• <strong>Für individuelle Angebote:</strong> Kontaktieren Sie uns – oft sind Rabatte bei speziellen Anforderungen möglich</li>
            <li>• <strong>Verfügbarkeit prüfen:</strong> Buchen Sie frühzeitig (min. 2 Wochen Vorlaufzeit empfohlen)</li>
          </ul>
        </div>
      </div>
    </div>
  </section>

  <!-- FAQ Section -->
  <section class="py-16 bg-gradient-to-br from-brand-primary/5 to-brand-accent/5">
    <div class="container mx-auto px-4">
      <div class="max-w-3xl mx-auto">
        <h2 class="text-4xl font-bold text-center mb-4 text-gray-900">Häufig gestellte Fragen</h2>
        <p class="text-center text-gray-700 mb-12">
          Noch Fragen? Wir beantworten gerne Ihre Fragen zur Mobile Videowand
        </p>

        <div class="space-y-4">
          <div *ngFor="let faq of faqs; let i = index" class="bg-white rounded-lg shadow-md overflow-hidden">
            <button
              (click)="toggleFAQ(i)"
              class="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition font-bold text-lg text-left text-gray-900"
            >
              <span>{{ faq.question }}</span>
              <span
                class="text-brand-accent text-2xl transform transition-transform"
                [class.rotate-45]="faq.open"
              >
                +
              </span>
            </button>
            <div *ngIf="faq.open" class="px-6 py-4 bg-gray-50 border-t border-gray-200">
              <p class="text-gray-700 leading-relaxed">{{ faq.answer }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>

  <!-- CTA Block -->
  <section class="bg-gradient-to-r from-brand-primary to-brand-accent text-white py-20 px-4">
    <div class="container mx-auto max-w-3xl text-center">
      <h2 class="text-4xl md:text-5xl font-bold mb-6">
        Bereit für Ihre Event-Lösung?
      </h2>
      <p class="text-xl mb-10 text-white/90">
        Verfügbarkeit prüfen, Angebot erhalten – in wenigen Klicks. Unser Team antwortet innerhalb von 24 Stunden.
      </p>
      <div class="flex flex-col sm:flex-row gap-4 justify-center">
        <button
          (click)="navigateToBooking()"
          class="inline-block bg-white text-brand-primary font-bold px-10 py-4 rounded-lg hover:shadow-2xl hover:scale-105 transition-all duration-300 text-lg"
        >
          Verfügbarkeit prüfen
        </button>
        <a
          href="mailto:info@videowand-leipzig.de"
          class="inline-block border-2 border-white text-white font-bold px-10 py-4 rounded-lg hover:bg-white hover:text-brand-primary transition-all duration-300 text-lg"
        >
          Direkt kontaktieren
        </a>
      </div>
    </div>
  </section>

  <!-- Kontaktoptionen Footer -->
  <section class="bg-gray-900 text-white py-12 px-4">
    <div class="container mx-auto max-w-4xl">
      <div class="grid md:grid-cols-3 gap-8 text-center mb-8">
        <div>
          <h3 class="text-lg font-bold mb-2">📞 Telefon</h3>
          <a href="tel:+493411234567" class="text-brand-accent hover:underline">
            +49 341 123 45 67
          </a>
        </div>
        <div>
          <h3 class="text-lg font-bold mb-2">✉️ E-Mail</h3>
          <a href="mailto:info@videowand-leipzig.de" class="text-brand-accent hover:underline">
            info@videowand-leipzig.de
          </a>
        </div>
        <div>
          <h3 class="text-lg font-bold mb-2">📍 Standort</h3>
          <p class="text-gray-300">Leipzig, Sachsen</p>
        </div>
      </div>
      <div class="border-t border-gray-700 pt-8 text-center text-gray-400 text-sm">
        <p>
          <a routerLink="/" class="text-brand-accent hover:underline">← Zurück zur Startseite</a>
          •
          <a href="/privacy" class="text-gray-300 hover:text-white">Datenschutz</a>
          •
          <a href="/imprint" class="text-gray-300 hover:text-white">Impressum</a>
        </p>
        <p class="mt-4">© 2024 Videowand Leipzig. Alle Rechte vorbehalten.</p>
      </div>
    </div>
  </section>
</div>
